import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { AppContext } from '../../context/AppContext';
import { 
  Calendar, 
  Receipt, 
  Camera, 
  UploadCloud, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  FileText,
  Sparkles,
  Check,
  CreditCard,
  Scan,
  MessageSquare,
  X
} from 'lucide-react';
import { scanReceiptOcr, CARD_COMPANIES } from '../../utils/receiptOcr';

const MobileLeaveExpenseTab = ({ initialMode = 'leave' }) => {
  const { 
    currentUser, 
    users, 
    applyForLeave, 
    applyForExpense, 
    withdrawLeaveRequest,
    withdrawExpenseRequest,
    requestLeaveCancellation,
    approvals,
    corporateCards,
    activeFiscalYear,
    getAutoApprovalLine
  } = useContext(AppContext);

  const user = users.find(u => u.UserID === currentUser?.UserID) || currentUser;
  const todayStr = new Date().toISOString().slice(0, 10);
  const [activeSubTab, setActiveSubTab] = useState(initialMode); // 'leave' | 'expense' | 'history'

  useEffect(() => {
    if (initialMode) {
      setActiveSubTab(initialMode);
    }
  }, [initialMode]);

  // Leave Form State
  const [leaveType, setLeaveType] = useState('FULL'); // 'FULL' | 'HALF_AM' | 'HALF_PM' | 'HALF'
  const [leaveStartDate, setLeaveStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [leaveEndDate, setLeaveEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [leaveReason, setLeaveReason] = useState('');
  const [sendKakaoLeave, setSendKakaoLeave] = useState(true); // Default ON
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);

  // Expense Form State
  const [expenseCategory, setExpenseCategory] = useState('식대');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().slice(0, 10));
  const [expenseDetails, setExpenseDetails] = useState('');
  const [sendKakaoExpense, setSendKakaoExpense] = useState(true); // Default ON
  const [cardCompany, setCardCompany] = useState('국민카드');
  const [customCardCompany, setCustomCardCompany] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [isOcrScanning, setIsOcrScanning] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrSuccess, setOcrSuccess] = useState(false);
  const [isSubmittingExpense, setIsSubmittingExpense] = useState(false);
  const [selectedExpenseDetail, setSelectedExpenseDetail] = useState(null);
  const [selectedLeaveDetail, setSelectedLeaveDetail] = useState(null);
  const [cancelModalItem, setCancelModalItem] = useState(null);
  const [cancelReasonInput, setCancelReasonInput] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  const mobileCameraRef = useRef(null);
  const mobileGalleryRef = useRef(null);

  // Leave calculations
  const myLeaveApprovals = useMemo(() => {
    return (approvals || []).filter(a => a.Type === 'LEAVE' && (a.ApplicantID === currentUser?.UserID || a.ApplicantName === currentUser?.Name));
  }, [approvals, currentUser]);

  const pendingLeaveList = myLeaveApprovals.filter(a => a.Status === 'PENDING');
  const pendingLeaveDays = pendingLeaveList.reduce((sum, a) => sum + (Number(a.LeaveDays) || 0), 0);
  const totalLeave = (user.TotalLeave !== undefined && user.TotalLeave !== null && user.TotalLeave !== '') ? Number(user.TotalLeave) : 15;
  const usedLeave = Number(user.UsedLeave || 0);
  const baseRemaining = (user.RemainingLeave !== undefined && user.RemainingLeave !== null && user.RemainingLeave !== '') ? Number(user.RemainingLeave) : (totalLeave - usedLeave);
  const realRemainingDays = baseRemaining - pendingLeaveDays;

  const isHalf = leaveType === 'HALF' || leaveType === 'HALF_AM' || leaveType === 'HALF_PM';

  // Calculate deduction
  const getLeaveDeduction = () => {
    if (leaveType === 'FULL') {
      const s = new Date(leaveStartDate);
      const e = new Date(leaveEndDate);
      const diff = Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
      return diff;
    }
    return 0.5;
  };

  const days = getLeaveDeduction();
  const projectedRemainingDays = Math.max(0, realRemainingDays - days);
  const requestedTypeLabel = leaveType === 'HALF_AM' ? '오전반차' : leaveType === 'HALF_PM' ? '오후반차' : leaveType === 'HALF' ? '반차' : '연차';

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingLeave) return;
    if (!leaveReason) {
      alert('연차 사유를 입력해 주세요.');
      return;
    }
    setIsSubmittingLeave(true);
    try {
      const start = leaveStartDate;
      const end = isHalf ? leaveStartDate : leaveEndDate;
      const totalCalendar = isHalf ? 1 : Math.max(1, Math.round((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1);

      applyForLeave(
        days,
        leaveReason,
        start,
        end,
        leaveType,
        false, // isSpecial
        '',    // specialType
        '',    // proofFileName
        '',    // proofUrl
        totalCalendar,
        '',
        null,
        sendKakaoLeave
      );

      alert('연차 신청서가 정상적으로 제출되었습니다.');
      setLeaveReason('');
      setActiveSubTab('history');
    } catch (err) {
      alert('연차 신청 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  const handleReceiptCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
    setOcrSuccess(false);

    // Run AI OCR
    setIsOcrScanning(true);
    setOcrProgress(15);
    try {
      const result = await scanReceiptOcr(file, (progress) => {
        setOcrProgress(progress);
      });

      if (result) {
        if (result.amount) setExpenseAmount(result.amount.toString());
        if (result.date) setExpenseDate(result.date);
        if (result.merchant) setExpenseDetails(result.merchant);
        if (result.cardCompany) {
          if (CARD_COMPANIES.includes(result.cardCompany)) {
            setCardCompany(result.cardCompany);
          } else {
            setCardCompany('직접 입력');
            setCustomCardCompany(result.cardCompany);
          }
        }
        if (result.cardNumber) setCardNumber(result.cardNumber);
        setOcrSuccess(true);
      }
    } catch (ocrErr) {
      console.warn('OCR Scan failed:', ocrErr);
    } finally {
      setIsOcrScanning(false);
      setOcrProgress(0);
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingExpense) return;
    if (!expenseAmount || !expenseDetails) {
      alert('금액과 사용처를 입력해 주세요.');
      return;
    }

    setIsSubmittingExpense(true);
    try {
      const finalCardCompany = cardCompany === '직접 입력' ? (customCardCompany.trim() || '기타카드') : cardCompany;
      let receiptUrl = '';

      if (receiptFile) {
        try {
          const resp = await fetch('https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/expense/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ApplicantID: currentUser?.UserID,
              Type: 'EXPENSE',
              Amount: Number(expenseAmount),
              FileName: receiptFile.name
            })
          });

          if (resp.ok) {
            const data = await resp.json();
            await fetch(data.UploadUrl, {
              method: 'PUT',
              body: receiptFile,
              headers: { 'Content-Type': receiptFile.type || 'application/octet-stream' }
            });
            receiptUrl = data.ReceiptS3Url;
          }
        } catch (uploadErr) {
          console.warn('Receipt upload failed, continuing with local claim:', uploadErr);
        }
      }

      applyForExpense(
        Number(expenseAmount),
        `[${expenseCategory}] ${expenseDetails}`,
        finalCardCompany,
        cardNumber.trim(),
        receiptFile?.name || '',
        receiptUrl,
        '',
        null,
        sendKakaoExpense,
        expenseDate
      );

      alert('경비 결재 요청이 성공적으로 접수되었습니다.');
      setExpenseAmount('');
      setExpenseDetails('');
      setCardNumber('');
      setCustomCardCompany('');
      setReceiptFile(null);
      setReceiptPreview(null);
      setOcrSuccess(false);
      setActiveSubTab('history');
    } catch (err) {
      alert('경비 신청 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setIsSubmittingExpense(false);
    }
  };

  // My approvals & Period/Search Filter State (Requirement 6)
  const [historyPeriodFilter, setHistoryPeriodFilter] = useState('THIS_MONTH'); // 'THIS_MONTH' | '3_MONTHS' | '6_MONTHS' | 'ALL'
  const [historySearchTerm, setHistorySearchTerm] = useState('');

  const myApprovals = approvals.filter(a => a.ApplicantID === currentUser?.UserID);

  const filteredMyApprovals = useMemo(() => {
    const now = new Date();
    return myApprovals.filter(item => {
      const d = new Date(item.CreatedAt);

      if (historyPeriodFilter === 'THIS_MONTH') {
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
      } else if (historyPeriodFilter === '3_MONTHS') {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        if (d < threeMonthsAgo) return false;
      } else if (historyPeriodFilter === '6_MONTHS') {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        if (d < sixMonthsAgo) return false;
      }

      if (historySearchTerm.trim()) {
        const term = historySearchTerm.toLowerCase();
        const detailsMatch = (item.Details || item.Reason || '').toLowerCase().includes(term);
        const cardMatch = (item.CardCompany || '').toLowerCase().includes(term);
        const amountMatch = (item.Amount || '').toString().includes(term);
        if (!detailsMatch && !cardMatch && !amountMatch) return false;
      }

      return true;
    }).sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));
  }, [myApprovals, historyPeriodFilter, historySearchTerm]);

  return (
    <div className="p-4 space-y-4 text-left pb-20 bg-slate-50 min-h-full">
      
      {/* Subtab Segmented Switch */}
      <div className="flex bg-slate-200/80 p-1 rounded-2xl">
        {[
          { id: 'leave', label: '🌴 연차 신청' },
          { id: 'expense', label: '💳 경비 청구' },
          { id: 'history', label: '📑 내 신청 이력' }
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${
              activeSubTab === tab.id
                ? 'bg-white text-indigo-700 shadow-2xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 1. Leave Form */}
      {activeSubTab === 'leave' && (
        <form onSubmit={handleLeaveSubmit} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
          
          {/* 4-Tier Mobile Metric Cards */}
          <div className="grid grid-cols-2 gap-2 text-left pb-1">
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
              <span className="text-[10px] font-bold text-slate-400 uppercase block">총 부여</span>
              <span className="text-base font-black text-slate-900">{totalLeave}일</span>
            </div>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
              <span className="text-[10px] font-bold text-slate-400 uppercase block">사용 완료</span>
              <span className="text-base font-black text-slate-900">{usedLeave}일</span>
            </div>
            <div className="bg-amber-50/70 p-3 rounded-xl border border-amber-200/80">
              <span className="text-[10px] font-bold text-amber-600 uppercase block">결재 대기 중</span>
              <span className="text-base font-black text-amber-600">{pendingLeaveDays}일 ({pendingLeaveList.length}건)</span>
            </div>
            <div className="bg-emerald-50/80 p-3 rounded-xl border border-emerald-200/90">
              <span className="text-[10px] font-bold text-emerald-700 uppercase block">실사용 가능 잔여</span>
              <span className="text-base font-black text-emerald-600">{realRemainingDays.toFixed(1)}일</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-bold text-slate-400 uppercase">휴가 유형 선택</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setLeaveType('FULL'); setLeaveEndDate(leaveStartDate); }}
                className={`py-2.5 px-3 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                  leaveType === 'FULL'
                    ? 'bg-indigo-50 border-indigo-600 text-indigo-700 shadow-2xs font-black'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}
              >
                연차 (1일)
              </button>
              <button
                type="button"
                onClick={() => {
                  setLeaveType(prev => (prev === 'HALF_PM' ? 'HALF_PM' : 'HALF_AM'));
                  setLeaveEndDate(leaveStartDate);
                }}
                className={`py-2.5 px-3 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                  isHalf
                    ? 'bg-indigo-50 border-indigo-600 text-indigo-700 shadow-2xs font-black'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}
              >
                반차 (0.5일)
              </button>
            </div>

            {/* Sub-selection for Half Day: AM / PM */}
            {isHalf && (
              <div className="p-2 bg-indigo-50/80 rounded-xl border border-indigo-100 flex items-center justify-around gap-2 text-xs">
                <label className={`flex items-center gap-1.5 font-bold cursor-pointer py-1.5 px-3 rounded-lg transition-all ${leaveType === 'HALF_AM' || leaveType === 'HALF' ? 'bg-white text-indigo-700 shadow-2xs border border-indigo-200' : 'text-slate-600'}`}>
                  <input
                    type="radio"
                    name="mobileHalfType"
                    value="HALF_AM"
                    checked={leaveType === 'HALF_AM' || leaveType === 'HALF'}
                    onChange={() => setLeaveType('HALF_AM')}
                    className="w-3.5 h-3.5 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>☀️ 오전반차</span>
                </label>
                <label className={`flex items-center gap-1.5 font-bold cursor-pointer py-1.5 px-3 rounded-lg transition-all ${leaveType === 'HALF_PM' ? 'bg-white text-indigo-700 shadow-2xs border border-indigo-200' : 'text-slate-600'}`}>
                  <input
                    type="radio"
                    name="mobileHalfType"
                    value="HALF_PM"
                    checked={leaveType === 'HALF_PM'}
                    onChange={() => setLeaveType('HALF_PM')}
                    className="w-3.5 h-3.5 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>🌙 오후반차</span>
                </label>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase">
              {leaveType === 'FULL' ? '기간 선택 (시작일 ~ 종료일)' : '사용 일자'}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={leaveStartDate}
                onChange={e => setLeaveStartDate(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-indigo-600"
              />
              {leaveType === 'FULL' && (
                <>
                  <span className="text-slate-400 font-bold">~</span>
                  <input
                    type="date"
                    value={leaveEndDate}
                    onChange={e => setLeaveEndDate(e.target.value)}
                    className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-indigo-600"
                  />
                </>
              )}
            </div>
          </div>

          {/* Real-time Calculation Banner */}
          <div className="p-3 bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 rounded-xl border border-indigo-100 space-y-1.5">
            <div className="flex justify-between items-center text-xs">
              <span className="font-extrabold text-slate-800 flex items-center gap-1">
                <span>📊</span> 실시간 차감 계산
              </span>
              <span className="text-[10px] font-bold text-indigo-700 bg-white px-2 py-0.5 rounded-md border border-indigo-100 shadow-2xs">
                {requestedTypeLabel} ({days}일 차감)
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-xs bg-white/90 p-2 rounded-lg border border-indigo-100/60 text-center">
              <div>
                <span className="text-[9px] text-slate-400 font-bold block">현재 잔여</span>
                <span className="font-black text-xs text-slate-900">{realRemainingDays.toFixed(1)}일</span>
              </div>
              <div className="border-x border-slate-100">
                <span className="text-[9px] text-indigo-500 font-bold block">이번 신청</span>
                <span className="font-black text-xs text-indigo-600">-{days}일</span>
              </div>
              <div>
                <span className="text-[9px] text-emerald-600 font-bold block">예상 잔여</span>
                <span className={`font-black text-xs ${projectedRemainingDays < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {projectedRemainingDays.toFixed(1)}일
                </span>
              </div>
            </div>
            {projectedRemainingDays < 0 && (
              <p className="text-[10px] text-rose-600 font-bold text-left pt-0.5">
                ⚠️ 잔여 연차를 초과하여 신청할 수 없습니다.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase">사유</label>
            <textarea
              placeholder="휴가 사유를 간단히 입력하세요 (예: 개인 사유, 병원 진료)"
              value={leaveReason}
              onChange={e => setLeaveReason(e.target.value)}
              className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 resize-none h-20"
              required
            />
          </div>

          <div className="p-3 bg-indigo-50/60 rounded-xl text-xs text-indigo-900 font-medium flex justify-between items-center">
            <span>차감 예정 일수:</span>
            <strong className="text-sm font-black text-indigo-700">{getLeaveDeduction()}일</strong>
          </div>

          {/* KakaoTalk Notification Toggle (Default ON) */}
          <div 
            onClick={() => setSendKakaoLeave(prev => !prev)}
            className="bg-amber-50/80 border border-amber-200/90 p-3 rounded-2xl flex items-center justify-between shadow-2xs cursor-pointer active:bg-amber-100/70 transition-all select-none"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber-400 text-slate-950 flex items-center justify-center font-bold shadow-2xs flex-shrink-0">
                <MessageSquare className="w-3.5 h-3.5 text-slate-900" />
              </div>
              <div>
                <span className="text-xs font-bold text-slate-900 block">결재 담당자에게 카톡 알림 전송</span>
                <span className="text-[10px] text-slate-500">결재자에게 카카오톡으로 결재 요청 알림 발송</span>
              </div>
            </div>
            <div className="relative inline-flex items-center flex-shrink-0 ml-2">
              <input
                type="checkbox"
                checked={sendKakaoLeave}
                onChange={e => setSendKakaoLeave(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-9 h-5 rounded-full transition-all relative ${sendKakaoLeave ? 'bg-amber-500' : 'bg-slate-300'}`}>
                <div className={`w-4 h-4 bg-white rounded-full absolute top-[2px] transition-all shadow-xs ${sendKakaoLeave ? 'right-[2px]' : 'left-[2px]'}`} />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmittingLeave}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs shadow-md transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmittingLeave ? '제출 중...' : '연차 신청서 제출'}
          </button>
        </form>
      )}

      {/* Recent Leave History under Leave Tab */}
      {activeSubTab === 'leave' && myLeaveApprovals.length > 0 && (
        <div className="space-y-2 pt-2">
          <div className="flex justify-between items-center px-1">
            <span className="text-xs font-black text-slate-800 flex items-center gap-1.5">
              <span>📑</span> 최근 연차 신청 내역
            </span>
            <button
              onClick={() => setActiveSubTab('history')}
              className="text-[11px] text-indigo-600 font-bold hover:underline"
            >
              전체 이력 보기 ❯
            </button>
          </div>

          <div className="space-y-2">
            {myLeaveApprovals.slice(0, 5).map(item => (
              <div 
                key={item.ApprovalID}
                onClick={() => setSelectedLeaveDetail(item)}
                className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-2xs space-y-2 cursor-pointer active:scale-98 transition-all"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1.5">
                    {item.LeaveType === 'HALF_AM' ? (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-sky-100 text-sky-800">
                        ☀️ 오전반차
                      </span>
                    ) : item.LeaveType === 'HALF_PM' ? (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800">
                        🌙 오후반차
                      </span>
                    ) : item.LeaveType === 'HALF' ? (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-100 text-indigo-800">
                        반차
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-100 text-indigo-800">
                        연차
                      </span>
                    )}
                    <span className="font-extrabold text-slate-900 text-xs">
                      {item.StartDate} {item.StartDate !== item.EndDate ? `~ ${item.EndDate}` : ''} ({item.Days || item.LeaveDays}일)
                    </span>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    item.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                    item.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800' :
                    item.Status === 'CANCEL_REQUESTED' ? 'bg-amber-100 text-amber-800' :
                    item.Status === 'CANCELED' ? 'bg-slate-100 text-slate-500' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {item.Status === 'APPROVED' ? '승인 완료' : 
                     item.Status === 'REJECTED' ? '반려됨' : 
                     item.Status === 'CANCEL_REQUESTED' ? '취소 대기' :
                     item.Status === 'CANCELED' ? '취소 완료' :
                     '승인 대기'}
                  </span>
                </div>

                <p className="text-[11px] text-slate-600 truncate">{item.Reason || item.Details}</p>

                <div className="pt-1.5 border-t border-slate-100 flex justify-between items-center">
                  <span className="text-[10px] text-slate-400">신청일: {new Date(item.CreatedAt).toLocaleDateString()}</span>
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    {item.Status === 'APPROVED' && (
                      item.StartDate >= todayStr ? (
                        <button
                          onClick={() => {
                            setCancelModalItem(item);
                            setCancelReasonInput('');
                          }}
                          className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-lg text-[10px] font-black transition-all cursor-pointer"
                        >
                          취소 신청
                        </button>
                      ) : (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-400 rounded text-[10px] font-bold">
                          사용완료
                        </span>
                      )
                    )}
                    {item.Status === 'PENDING' && (
                      <button
                        onClick={() => {
                          if (confirm('대기 중인 연차 신청을 회수하시겠습니까?')) {
                            withdrawLeaveRequest(item.ApprovalID);
                          }
                        }}
                        className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 rounded-lg text-[10px] font-bold transition-all cursor-pointer"
                      >
                        신청 회수
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedLeaveDetail(item)}
                      className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-[10px] font-black transition-all cursor-pointer"
                    >
                      상세보기
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. Expense Form with Camera OCR */}
      {activeSubTab === 'expense' && (
        <form onSubmit={handleExpenseSubmit} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
          
          {/* Camera Capture Inputs */}
          <input
            type="file"
            ref={mobileCameraRef}
            onChange={handleReceiptCapture}
            accept="image/*"
            capture="environment"
            className="hidden"
          />
          <input
            type="file"
            ref={mobileGalleryRef}
            onChange={handleReceiptCapture}
            accept="image/*,.pdf"
            className="hidden"
          />

          {/* Camera OCR Action Buttons */}
          <div className="space-y-2">
            <label className="text-[11px] font-bold text-slate-400 uppercase block">영수증 등록 & AI 자동 인식</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => mobileCameraRef.current?.click()}
                className="py-3 px-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs cursor-pointer active:scale-95 transition-transform"
              >
                <Camera className="w-4 h-4" />
                <span>영수증 직접 촬영</span>
              </button>
              <button
                type="button"
                onClick={() => mobileGalleryRef.current?.click()}
                className="py-3 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 cursor-pointer active:scale-95 transition-transform"
              >
                <UploadCloud className="w-4 h-4 text-indigo-600" />
                <span>앨범/파일 선택</span>
              </button>
            </div>
          </div>

          {/* Scanning Futuristic Hologram Animation */}
          {isOcrScanning && (
            <div className="p-3.5 bg-indigo-950/95 text-white border border-indigo-500/50 rounded-2xl space-y-2.5 shadow-lg relative overflow-hidden backdrop-blur-md animate-fade-in">
              <div className="flex justify-between items-center text-xs font-bold relative z-10">
                <div className="flex items-center gap-2">
                  <Scan className="w-4 h-4 animate-spin text-cyan-400" />
                  <div>
                    <span className="text-cyan-300 font-extrabold block text-xs">✨ AI 영수증 분석 중</span>
                    <span className="text-[10px] text-slate-300 font-medium">
                      {ocrProgress < 40 ? '📷 [1/3] 이미지 전처리 및 문자 검출' :
                       ocrProgress < 80 ? '🔍 [2/3] OCR 가맹점/금액 정밀 추출' :
                       '💳 [3/3] 카드사 및 결제 정보 매칭 완료'}
                    </span>
                  </div>
                </div>
                <span className="font-mono text-sm font-black text-cyan-300 px-2 py-0.5 bg-indigo-900 border border-cyan-500/40 rounded-lg">
                  {ocrProgress}%
                </span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 via-cyan-400 to-emerald-400 h-full rounded-full transition-all duration-300 shadow-[0_0_8px_rgba(6,182,212,0.8)]" style={{ width: `${Math.max(ocrProgress, 15)}%` }} />
              </div>
            </div>
          )}

          {ocrSuccess && (
            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-xs font-bold text-emerald-800 animate-fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span>영수증에서 금액, 사용처, 카드사가 자동 입력되었습니다.</span>
            </div>
          )}

          {/* Receipt Preview Thumbnail with Laser Scanning Beam */}
          {receiptPreview && (
            <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between relative overflow-hidden">
              <div className="flex items-center gap-2.5 overflow-hidden">
                <div className="relative w-12 h-12 rounded-lg border border-slate-200 overflow-hidden shrink-0 bg-slate-900">
                  <img src={receiptPreview} alt="Receipt" className="w-full h-full object-cover" />
                  {isOcrScanning && (
                    <>
                      <div className="absolute inset-0 bg-indigo-500/20 pointer-events-none" />
                      <div className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_8px_#22d3ee] animate-laser-scan pointer-events-none" />
                    </>
                  )}
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-800 truncate block">영수증 첨부 완료</span>
                  <span className="text-[10px] text-slate-400 font-medium">자동 분석 연동됨</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setReceiptFile(null);
                  setReceiptPreview(null);
                  setOcrSuccess(false);
                }}
                className="text-xs text-rose-500 hover:text-rose-700 font-bold px-2.5 py-1 bg-rose-50 rounded-lg border border-rose-200 active:scale-95 transition-transform"
              >
                삭제
              </button>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase">경비 분류</label>
            <select
              value={expenseCategory}
              onChange={e => setExpenseCategory(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
            >
              <option value="식대">식대 (야근/중식 등)</option>
              <option value="교통비">교통비 / 택시비</option>
              <option value="소모품">사무용품 / 소모품</option>
              <option value="회의비">회의비 / 다과비</option>
              <option value="기타">기타 경비</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase">결제 금액 (₩) *</label>
            <input
              type="number"
              placeholder="예: 25000"
              value={expenseAmount}
              onChange={e => setExpenseAmount(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-slate-900 focus:outline-none focus:border-indigo-600"
              required
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase">사용처 / 가맹점명 *</label>
            <input
              type="text"
              placeholder="예: 스타벅스 강남점"
              value={expenseDetails}
              onChange={e => setExpenseDetails(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-indigo-600"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase">결제 카드사</label>
              <select
                value={cardCompany}
                onChange={e => setCardCompany(e.target.value)}
                className="w-full px-2.5 py-2 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-800 focus:outline-none"
              >
                {CARD_COMPANIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {cardCompany === '직접 입력' && (
                <input
                  type="text"
                  placeholder="카드사명 직접 입력"
                  value={customCardCompany}
                  onChange={e => setCustomCardCompany(e.target.value)}
                  className="w-full px-2.5 py-2 bg-white border border-indigo-400 rounded-lg text-xs text-slate-800 focus:outline-none"
                  required
                />
              )}
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-500 uppercase">카드 번호 (선택)</label>
              <input
                type="text"
                placeholder="예: 1234 또는 xxxx-xxxx"
                value={cardNumber}
                onChange={e => setCardNumber(e.target.value)}
                className="w-full px-2.5 py-2 bg-white border border-slate-300 rounded-lg text-xs text-slate-800 focus:outline-none"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-400 uppercase">결제 일자</label>
            <input
              type="date"
              value={expenseDate}
              onChange={e => setExpenseDate(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800"
            />
          </div>

          {/* KakaoTalk Notification Toggle (Default ON) */}
          <div 
            onClick={() => setSendKakaoExpense(prev => !prev)}
            className="bg-amber-50/80 border border-amber-200/90 p-3 rounded-2xl flex items-center justify-between shadow-2xs cursor-pointer active:bg-amber-100/70 transition-all select-none"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-amber-400 text-slate-950 flex items-center justify-center font-bold shadow-2xs flex-shrink-0">
                <MessageSquare className="w-3.5 h-3.5 text-slate-900" />
              </div>
              <div>
                <span className="text-xs font-bold text-slate-900 block">결재 담당자에게 카톡 알림 전송</span>
                <span className="text-[10px] text-slate-500">결재자에게 카카오톡으로 경비 청구 알림 발송</span>
              </div>
            </div>
            <div className="relative inline-flex items-center flex-shrink-0 ml-2">
              <input
                type="checkbox"
                checked={sendKakaoExpense}
                onChange={e => setSendKakaoExpense(e.target.checked)}
                className="sr-only"
              />
              <div className={`w-9 h-5 rounded-full transition-all relative ${sendKakaoExpense ? 'bg-amber-500' : 'bg-slate-300'}`}>
                <div className={`w-4 h-4 bg-white rounded-full absolute top-[2px] transition-all shadow-xs ${sendKakaoExpense ? 'right-[2px]' : 'left-[2px]'}`} />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmittingExpense}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs shadow-md transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmittingExpense ? '청구 중...' : '경비 결재 요청 제출'}
          </button>
        </form>
      )}

      {/* 3. My Approvals History (Requirement 5 & 6) */}
      {activeSubTab === 'history' && (
        <div className="space-y-3">
          {/* Period Filter Chips */}
          <div className="flex bg-slate-200/80 p-1 rounded-xl gap-1">
            {[
              { id: 'THIS_MONTH', label: '이번 달' },
              { id: '3_MONTHS', label: '3개월' },
              { id: '6_MONTHS', label: '6개월' },
              { id: 'ALL', label: '전체' }
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setHistoryPeriodFilter(tab.id);
                  setHistoryPage(1);
                }}
                className={`flex-1 py-1.5 text-[11px] font-bold rounded-lg transition-all ${
                  historyPeriodFilter === tab.id
                    ? 'bg-white text-indigo-700 shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="사용처, 사유, 금액 검색..."
              value={historySearchTerm}
              onChange={(e) => {
                setHistorySearchTerm(e.target.value);
                setHistoryPage(1);
              }}
              className="w-full pl-3 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-600 font-medium"
            />
            {historySearchTerm && (
              <button
                type="button"
                onClick={() => {
                  setHistorySearchTerm('');
                  setHistoryPage(1);
                }}
                className="absolute right-2.5 top-2 text-slate-400 p-0.5 text-xs"
              >
                ✕
              </button>
            )}
          </div>

          {/* Stats Bar */}
          <div className="flex justify-between items-center px-1 text-[11px] text-slate-500 font-semibold">
            <span>총 {filteredMyApprovals.length}건</span>
            <span>
              합계: <strong className="text-indigo-600 font-mono font-bold">
                ₩ {filteredMyApprovals.reduce((s, i) => s + (Number(i.Amount) || 0), 0).toLocaleString()}
              </strong>
            </span>
          </div>

          {filteredMyApprovals.length === 0 ? (
            <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl text-slate-400 text-xs font-medium">
              선택한 조건의 신청 이력이 없습니다.
            </div>
          ) : (
            filteredMyApprovals.slice((historyPage - 1) * ITEMS_PER_PAGE, historyPage * ITEMS_PER_PAGE).map(item => (
              <div 
                key={item.ApprovalID} 
                onClick={() => {
                  if (item.Type === 'EXPENSE') setSelectedExpenseDetail(item);
                  else setSelectedLeaveDetail(item);
                }}
                className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-2xs space-y-2 cursor-pointer active:scale-98 transition-all"
              >
                <div className="flex justify-between items-center">
                  <span className="text-xs font-black text-slate-900">
                    {item.Type === 'EXPENSE' ? '💳 경비 청구' : '🌴 연차 신청'}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    item.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                    item.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {item.Status === 'APPROVED' ? '승인 완료' : item.Status === 'REJECTED' ? '반려됨' : '결재 대기 중'}
                  </span>
                </div>

                <div className="text-xs text-slate-700">
                  {item.Type === 'EXPENSE' ? (
                    <div>
                      <span className="font-bold text-indigo-700 font-mono">{Number(item.Amount || 0).toLocaleString()}원</span>
                      <span className="text-slate-600 block text-[11px] mt-0.5">{item.Details}</span>
                      <div className="text-[10px] text-indigo-600 font-bold mt-1 flex items-center justify-between">
                        <span>💳 {item.CardCompany || '카드사'} {item.CardNumber ? `(${item.CardNumber})` : ''}</span>
                        <span>상세 보기 ❯</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        {item.LeaveType === 'HALF_AM' ? (
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-sky-100 text-sky-800 flex items-center gap-1">
                            <span>☀️</span> 오전반차
                          </span>
                        ) : item.LeaveType === 'HALF_PM' ? (
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 flex items-center gap-1">
                            <span>🌙</span> 오후반차
                          </span>
                        ) : item.LeaveType === 'HALF' ? (
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-100 text-indigo-800">
                            반차
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-100 text-indigo-800">
                            연차
                          </span>
                        )}
                        <span className="font-bold text-slate-900 text-xs">
                          {item.StartDate} {item.StartDate !== item.EndDate ? `~ ${item.EndDate}` : ''} ({item.Days || item.LeaveDays}일)
                        </span>
                      </div>
                      <span className="text-slate-500 block text-[11px] mt-0.5">{item.Reason || item.Details}</span>
                    </div>
                  )}
                </div>

                <div className="text-[10px] text-slate-400 pt-1.5 border-t border-slate-100 flex justify-between items-center">
                  <span>신청일: {new Date(item.CreatedAt).toLocaleDateString()}</span>
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    {item.Type === 'LEAVE' && item.Status === 'APPROVED' && (
                      item.StartDate >= todayStr ? (
                        <button
                          onClick={() => {
                            setCancelModalItem(item);
                            setCancelReasonInput('');
                          }}
                          className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-lg text-[10px] font-black transition-all cursor-pointer"
                        >
                          취소 신청
                        </button>
                      ) : (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-400 rounded text-[10px] font-bold">
                          사용완료
                        </span>
                      )
                    )}
                    {item.Status === 'PENDING' && (
                      <button
                        onClick={() => {
                          const label = item.Type === 'EXPENSE' ? '경비' : '연차';
                          if (confirm(`대기 중인 ${label} 결재 신청을 취소(회수)하시겠습니까?`)) {
                            if (item.Type === 'EXPENSE') {
                              withdrawExpenseRequest(item.ApprovalID);
                            } else {
                              withdrawLeaveRequest(item.ApprovalID);
                            }
                          }
                        }}
                        className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-lg text-[10px] font-bold transition-all cursor-pointer"
                      >
                        신청 취소
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (item.Type === 'EXPENSE') setSelectedExpenseDetail(item);
                        else setSelectedLeaveDetail(item);
                      }}
                      className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-lg text-[10px] font-black transition-all cursor-pointer"
                    >
                      상세보기
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Mobile Pagination */}
          {filteredMyApprovals.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-between pt-3 border-t border-slate-200 text-xs">
              <span className="text-[11px] text-slate-500 font-medium">
                {historyPage} / {Math.ceil(filteredMyApprovals.length / ITEMS_PER_PAGE)} 페이지
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                  disabled={historyPage === 1}
                  className="px-2.5 py-1 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 text-xs disabled:opacity-40"
                >
                  ‹ 이전
                </button>

                <button
                  type="button"
                  onClick={() => setHistoryPage(prev => Math.min(Math.ceil(filteredMyApprovals.length / ITEMS_PER_PAGE), prev + 1))}
                  disabled={historyPage === Math.ceil(filteredMyApprovals.length / ITEMS_PER_PAGE)}
                  className="px-2.5 py-1 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 text-xs disabled:opacity-40"
                >
                  다음 ›
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile Payment Detail Modal with Approval Pipeline (Requirement 5) */}
      {selectedExpenseDetail && (() => {
        const stages = (selectedExpenseDetail.ApprovalStages && selectedExpenseDetail.ApprovalStages.length > 0)
          ? selectedExpenseDetail.ApprovalStages
          : (typeof getAutoApprovalLine === 'function' ? getAutoApprovalLine({ UserID: selectedExpenseDetail.ApplicantID, Department: selectedExpenseDetail.ApplicantDept || '클라우드네이티브' }) : []);

        return (
          <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 animate-in slide-in-from-bottom duration-200">
              <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 px-5 py-3.5 flex justify-between items-center text-white">
                <div className="flex items-center gap-2">
                  <span className="text-base">💳</span>
                  <h3 className="font-bold text-sm">경비 결제 상세 및 결재 현황</h3>
                </div>
                <button
                  onClick={() => setSelectedExpenseDetail(null)}
                  className="text-white/80 hover:text-white text-lg p-1 cursor-pointer"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 space-y-3 text-left max-h-[75vh] overflow-y-auto">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-xs font-bold text-slate-500">결재 상태</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-extrabold ${
                    selectedExpenseDetail.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                    selectedExpenseDetail.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {selectedExpenseDetail.Status === 'APPROVED' ? '승인 완료' : selectedExpenseDetail.Status === 'REJECTED' ? '반려됨' : '결재 대기 중'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                    <div className="text-[10px] text-slate-400 font-bold mb-0.5">결제 금액</div>
                    <div className="text-sm font-black text-indigo-700 font-mono">
                      ₩ {selectedExpenseDetail.Amount?.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                    <div className="text-[10px] text-slate-400 font-bold mb-0.5">신청 일시</div>
                    <div className="font-bold text-slate-800">
                      {new Date(selectedExpenseDetail.CreatedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                    <div className="text-[10px] text-slate-400 font-bold mb-0.5">결제 카드사</div>
                    <div className="font-bold text-slate-800">
                      {selectedExpenseDetail.CardCompany || '카드사 정보 없음'}
                    </div>
                  </div>
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                    <div className="text-[10px] text-slate-400 font-bold mb-0.5">카드 번호</div>
                    <div className="font-mono font-bold text-slate-800">
                      {selectedExpenseDetail.CardNumber || '-'}
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-xs">
                  <div className="text-[10px] text-slate-400 font-bold mb-1">사용처 및 내역</div>
                  <div className="font-bold text-slate-900">{selectedExpenseDetail.Details}</div>
                </div>

                {/* Mobile Visual Approval Pipeline Timeline */}
                <div className="space-y-2.5 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-800">결재 진행 현황</span>
                    <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                      {stages.length}단계 결재 라인
                    </span>
                  </div>

                  <div className="relative pl-5 space-y-3 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                    {/* Step 0 */}
                    <div className="relative flex items-start gap-2.5">
                      <div className="absolute -left-5 top-0.5 w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[9px] font-bold shadow-xs">
                        ✓
                      </div>
                      <div>
                        <span className="text-xs font-extrabold text-slate-900">
                          {selectedExpenseDetail.ApplicantName || currentUser.Name}
                        </span>
                        <span className="text-[10px] text-slate-400 block font-mono">
                          신청 제출 • {new Date(selectedExpenseDetail.CreatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>

                    {/* Stages */}
                    {stages.map((stage, sIdx) => {
                      const isDone = stage.Status === 'APPROVED' || selectedExpenseDetail.Status === 'APPROVED';
                      const isRejected = stage.Status === 'REJECTED' || selectedExpenseDetail.Status === 'REJECTED';
                      const isCurrent = !isDone && !isRejected && (selectedExpenseDetail.CurrentStep === stage.Step || sIdx === 0);

                      return (
                        <div key={sIdx} className="relative flex items-start gap-2.5">
                          <div className={`absolute -left-5 top-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shadow-xs ${
                            isDone ? 'bg-emerald-500 text-white' :
                            isRejected ? 'bg-rose-500 text-white' :
                            isCurrent ? 'bg-indigo-600 text-white animate-pulse' :
                            'bg-slate-200 text-slate-500'
                          }`}>
                            {isDone ? '✓' : isRejected ? '✕' : stage.Step || sIdx + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-slate-900">
                                {stage.ApproverName || `결재자 ${sIdx + 1}단계`} ({stage.ApproverJobTitle || '결재자'})
                              </span>
                              <span className={`text-[9px] font-extrabold px-1.5 py-0.2 rounded-full ${
                                isDone ? 'bg-emerald-100 text-emerald-800' :
                                isRejected ? 'bg-rose-100 text-rose-800' :
                                isCurrent ? 'bg-amber-100 text-amber-800' :
                                'bg-slate-100 text-slate-400'
                              }`}>
                                {isDone ? '승인' : isRejected ? '반려' : isCurrent ? '대기 중' : '대기'}
                              </span>
                            </div>
                            {stage.ActionAt && (
                              <span className="text-[10px] text-slate-400 font-mono block">
                                {new Date(stage.ActionAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {(selectedExpenseDetail.ReceiptUrl || selectedExpenseDetail.ReceiptS3Url || selectedExpenseDetail.ProofUrl) && (
                  <div className="space-y-1.5 pt-2 border-t border-slate-100">
                    <div className="text-xs font-bold text-slate-700">영수증 증빙</div>
                    <div className="p-2.5 bg-slate-100 rounded-xl flex items-center justify-between">
                      <span className="text-xs text-slate-700 truncate max-w-[160px]">
                        {selectedExpenseDetail.ProofFileName || selectedExpenseDetail.FileName || '영수증_첨부.jpg'}
                      </span>
                      <a
                        href={selectedExpenseDetail.ReceiptUrl || selectedExpenseDetail.ReceiptS3Url || selectedExpenseDetail.ProofUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold"
                      >
                        영수증 보기
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center gap-2">
                {selectedExpenseDetail.Status === 'PENDING' && (selectedExpenseDetail.ApplicantID === currentUser.UserID || selectedExpenseDetail.ApplicantName === currentUser.Name) && (
                  <button
                    onClick={() => {
                      if (confirm('대기 중인 경비 결재 신청을 즉시 취소(회수)하시겠습니까?')) {
                        withdrawExpenseRequest(selectedExpenseDetail.ApprovalID);
                        setSelectedExpenseDetail(null);
                      }
                    }}
                    className="flex-1 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold cursor-pointer shadow-2xs"
                  >
                    신청 취소
                  </button>
                )}
                <button
                  onClick={() => setSelectedExpenseDetail(null)}
                  className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold cursor-pointer"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Selected Leave Detail Modal for Mobile */}
      {selectedLeaveDetail && (() => {
        const appr = selectedLeaveDetail;
        const isSpecial = Boolean(appr.IsSpecial);
        const reqDays = Number(appr.LeaveDays || appr.Days || 0);

        const otherPendingList = (approvals || []).filter(a => 
          a.Type === 'LEAVE' && 
          a.Status === 'PENDING' && 
          a.ApprovalID !== appr.ApprovalID &&
          (a.ApplicantID === currentUser.UserID || a.ApplicantName === currentUser.Name)
        );
        const otherPendingDays = otherPendingList.reduce((s, a) => s + (Number(a.LeaveDays || a.Days) || 0), 0);
        const allPendingDays = otherPendingDays + (appr.Status === 'PENDING' ? reqDays : 0);

        const isApproved = appr.Status === 'APPROVED';
        const beforeBalance = appr.BeforeLeaveBalance !== undefined
          ? Number(appr.BeforeLeaveBalance)
          : (isApproved
              ? (baseRemaining + (isSpecial ? 0 : reqDays))
              : (baseRemaining - otherPendingDays));

        const afterBalance = appr.AfterLeaveBalance !== undefined
          ? Number(appr.AfterLeaveBalance)
          : (isApproved
              ? baseRemaining
              : (isSpecial ? beforeBalance : (beforeBalance - reqDays)));

        const isCancelReq = appr.Status === 'CANCEL_REQUESTED' || appr.Status === 'CANCELED';
        const restoredBalance = realRemainingDays + reqDays;

        const stages = (appr.ApprovalStages && appr.ApprovalStages.length > 0)
          ? appr.ApprovalStages
          : (typeof getAutoApprovalLine === 'function' ? getAutoApprovalLine({ UserID: appr.ApplicantID, Department: appr.ApplicantDept || '클라우드네이티브' }) : []);

        const leaveTypeName = isSpecial ? (appr.SpecialType || '특별휴가') : (
          appr.LeaveType === 'HALF_AM' ? '오전반차 (09:00~13:00)' :
          appr.LeaveType === 'HALF_PM' ? '오후반차 (14:00~18:00)' :
          appr.LeaveType === 'HALF' ? '반차 (0.5일)' :
          appr.LeaveType === 'QUARTER' ? '반반차 (0.25일)' : '연차'
        );

        return (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 animate-in slide-in-from-bottom duration-200 flex flex-col max-h-[85vh]">
              
              {/* Header */}
              <div className="p-4 bg-gradient-to-r from-indigo-700 to-indigo-900 text-white flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🌴</span>
                  <div>
                    <h3 className="font-extrabold text-sm text-white">연차 신청 및 결재선 상세</h3>
                    <p className="text-[10px] text-indigo-200">Leave Details & Approval Status</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedLeaveDetail(null)}
                  className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-4 overflow-y-auto space-y-3.5 text-xs text-left flex-1">
                
                {/* 4-Tier Leave Overview */}
                <div className="space-y-1">
                  <span className="text-[10px] font-extrabold text-slate-500 uppercase">내 연차 현황</span>
                  <div className="grid grid-cols-4 gap-1 text-center">
                    <div className="bg-slate-50 p-1.5 rounded-xl border border-slate-200">
                      <span className="text-[8px] text-slate-400 font-bold block">총부여</span>
                      <span className="font-black text-xs text-slate-900">{totalLeave}일</span>
                    </div>
                    <div className="bg-slate-50 p-1.5 rounded-xl border border-slate-200">
                      <span className="text-[8px] text-slate-400 font-bold block">기사용</span>
                      <span className="font-black text-xs text-slate-900">{usedLeave}일</span>
                    </div>
                    <div className="bg-amber-50 p-1.5 rounded-xl border border-amber-200">
                      <span className="text-[8px] text-amber-600 font-bold block">대기</span>
                      <span className="font-black text-xs text-amber-600">{allPendingDays}일</span>
                    </div>
                    <div className="bg-emerald-50 p-1.5 rounded-xl border border-emerald-200">
                      <span className="text-[8px] text-emerald-700 font-bold block">실잔여</span>
                      <span className="font-black text-xs text-emerald-600">{realRemainingDays.toFixed(1)}일</span>
                    </div>
                  </div>
                </div>

                {/* Real-time Calculation Banner */}
                {isCancelReq ? (
                  <div className="p-2.5 bg-emerald-50 rounded-2xl border border-emerald-200 space-y-1 text-center">
                    <span className="text-[10px] font-bold text-emerald-800 block">🔄 취소 시 연차 환원</span>
                    <div className="grid grid-cols-3 gap-1 bg-white p-1.5 rounded-xl border border-emerald-100">
                      <div>
                        <span className="text-[8px] text-slate-400 block">현재</span>
                        <span className="font-bold text-xs">{realRemainingDays.toFixed(1)}일</span>
                      </div>
                      <div>
                        <span className="text-[8px] text-emerald-600 block">환원</span>
                        <span className="font-bold text-xs text-emerald-600">+{reqDays}일</span>
                      </div>
                      <div>
                        <span className="text-[8px] text-emerald-700 block">복구후</span>
                        <span className="font-black text-xs text-emerald-700">{restoredBalance.toFixed(1)}일</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-2.5 bg-indigo-50 rounded-2xl border border-indigo-100 space-y-1 text-center">
                    <span className="text-[10px] font-bold text-indigo-900 block">📊 연차 차감 계산</span>
                    <div className="grid grid-cols-3 gap-1 bg-white p-1.5 rounded-xl border border-indigo-100">
                      <div>
                        <span className="text-[8px] text-slate-400 block">신청 전</span>
                        <span className="font-bold text-xs">{beforeBalance.toFixed(1)}일</span>
                      </div>
                      <div>
                        <span className="text-[8px] text-indigo-600 block">차감</span>
                        <span className="font-bold text-xs text-indigo-600">{isSpecial ? '0일' : `-${reqDays}일`}</span>
                      </div>
                      <div>
                        <span className="text-[8px] text-emerald-600 block">승인 후</span>
                        <span className={`font-black text-xs ${afterBalance < 0 && !isSpecial ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {afterBalance.toFixed(1)}일
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Details */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-slate-500 font-bold">휴가 구분:</span>
                    <span className="font-black text-indigo-700">{leaveTypeName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 font-bold">신청 기간:</span>
                    <span className="font-black text-slate-800">{appr.StartDate} {appr.StartDate !== appr.EndDate && `~ ${appr.EndDate}`} ({reqDays}일)</span>
                  </div>
                  <div className="pt-1">
                    <span className="text-slate-500 font-bold block mb-0.5">신청 사유:</span>
                    <p className="p-2 bg-white rounded-xl border border-slate-200 text-slate-700 font-medium leading-relaxed">
                      {appr.Details || appr.Reason}
                    </p>
                  </div>
                </div>

                {/* Approval Stages */}
                <div className="space-y-1.5 pt-1">
                  <span className="text-[10px] font-extrabold text-slate-600 uppercase block">결재 진행 현황</span>
                  <div className="space-y-1.5">
                    {stages.map((st, idx) => {
                      const isDone = st.Status === 'APPROVED' || (appr.Status === 'APPROVED' && (st.Step || idx + 1) <= (appr.CurrentStep || 1));
                      const isRej = st.Status === 'REJECTED' || appr.Status === 'REJECTED';
                      const isCur = appr.Status === 'PENDING' && Number(appr.CurrentStep || 1) === (st.Step || idx + 1);

                      return (
                        <div key={idx} className="p-2 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
                          <div>
                            <span className="font-bold text-slate-800">{st.ApproverName || `결재자 ${idx + 1}`} ({st.ApproverJobTitle || '결재자'})</span>
                            {st.ActionAt && (
                              <span className="text-[9px] text-slate-400 block font-mono">
                                {new Date(st.ActionAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full ${
                            isDone ? 'bg-emerald-100 text-emerald-800' :
                            isRej ? 'bg-rose-100 text-rose-800' :
                            isCur ? 'bg-amber-100 text-amber-800' :
                            'bg-slate-200 text-slate-500'
                          }`}>
                            {isDone ? '승인' : isRej ? '반려' : isCur ? '대기 중' : '대기'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Proof */}
                {appr.ProofUrl && (
                  <div className="pt-1">
                    <a
                      href={appr.ProofUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold flex items-center justify-center gap-1"
                    >
                      <span>📎</span>
                      <span>증빙 서류 열기</span>
                    </a>
                  </div>
                )}

                {/* Cancel Request Action in Modal */}
                {appr.Status === 'APPROVED' && (
                  <div className="pt-2 border-t border-slate-100">
                    {appr.StartDate >= todayStr ? (
                      <button
                        onClick={() => {
                          setSelectedLeaveDetail(null);
                          setCancelModalItem(appr);
                          setCancelReasonInput('');
                        }}
                        className="w-full py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-xl font-extrabold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                      >
                        <span>🔄</span>
                        <span>이 연차 취소 신청하기 (결재 상신)</span>
                      </button>
                    ) : (
                      <div className="text-center py-2 bg-slate-100 rounded-xl text-slate-400 font-bold text-xs">
                        이미 일정이 경과하여 사용 완료된 연차입니다.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-3 bg-slate-50 border-t border-slate-100">
                <button
                  onClick={() => setSelectedLeaveDetail(null)}
                  className="w-full py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold cursor-pointer"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Mobile Cancellation Reason Modal */}
      {cancelModalItem && (() => {
        const appr = cancelModalItem;
        const reqDays = Number(appr.LeaveDays || appr.Days || 0);

        return (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 animate-in slide-in-from-bottom duration-200 flex flex-col">
              <div className="p-4 bg-gradient-to-r from-rose-600 to-rose-800 text-white flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🔄</span>
                  <div>
                    <h3 className="font-extrabold text-sm text-white">연차 취소 신청</h3>
                    <p className="text-[10px] text-rose-200">결재권자 승인 시 연차가 자동 복구됩니다.</p>
                  </div>
                </div>
                <button
                  onClick={() => setCancelModalItem(null)}
                  className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-4 space-y-3 text-xs text-left">
                <div className="p-3 bg-rose-50 border border-rose-100 rounded-2xl space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500 font-bold">대상 기간:</span>
                    <span className="font-black text-slate-900">{appr.StartDate} {appr.StartDate !== appr.EndDate && `~ ${appr.EndDate}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 font-bold">환원 일수:</span>
                    <span className="font-black text-emerald-700 bg-white px-2 py-0.5 rounded border border-emerald-200">+{reqDays}일 복구</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="font-extrabold text-slate-700 block">
                    취소 사유 <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    value={cancelReasonInput}
                    onChange={e => setCancelReasonInput(e.target.value)}
                    placeholder="연차 취소 사유를 입력하세요 (예: 일정 변경 등)"
                    rows={3}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 font-medium"
                  />
                </div>
              </div>

              <div className="p-3 bg-slate-50 border-t border-slate-100 flex gap-2">
                <button
                  onClick={() => setCancelModalItem(null)}
                  className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold cursor-pointer"
                >
                  닫기
                </button>
                <button
                  onClick={() => {
                    if (!cancelReasonInput.trim()) {
                      alert('취소 사유를 입력해 주세요.');
                      return;
                    }
                    requestLeaveCancellation(appr.ApprovalID, cancelReasonInput.trim());
                    setCancelModalItem(null);
                    alert('연차 취소 결재가 상신되었습니다.\n결재권자의 승인이 완료되면 연차가 자동 복구됩니다.');
                  }}
                  className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-black shadow-md cursor-pointer"
                >
                  취소 결재 상신
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MobileLeaveExpenseTab;
